import React, { createContext, PropsWithChildren, useContext } from 'react';
import { useAllTenantFeatureFlagsQuery } from 'src/services/gql/generated';

export type FeatureFlagState = Readonly<{
  isAP2Enabled: boolean;
  isFlagVersion19: boolean;
  isFlagVersion195: boolean;
  loaded: boolean;
  isMultilevelTokenFlowForEmailEnabled: boolean;
  canRequire2FA: boolean;
  canFilterOnTag: boolean;
  isCM3Enabled: boolean;
  isNotifiHostedEnabled: boolean;
  isFlagVersionQueryFailed: boolean;
  isDiscordGroupsEnabled: boolean;
}>;

const FeatureFlagContext = createContext<FeatureFlagState>({
  isAP2Enabled: false,
  isFlagVersion19: false,
  isFlagVersion195: false,
  loaded: false,
  isMultilevelTokenFlowForEmailEnabled: false,
  canRequire2FA: false,
  canFilterOnTag: false,
  isCM3Enabled: false,
  isNotifiHostedEnabled: false,
  isFlagVersionQueryFailed: false,
  isDiscordGroupsEnabled: false
});

function parseFeatureFlagToBoolean(value: string | undefined) {
  if (value === 'false') return false;
  return !!value;
}

function getFlagValue(flags: Array<{ name: string; value: string }> | undefined, flagName: string): string | undefined {
  return flags?.find(flag => flag.name === flagName)?.value;
}

export const FeatureFlagProvider: React.FC<
  PropsWithChildren<Record<string, unknown>>
> = ({ children }) => {
  const { data, loading } = useAllTenantFeatureFlagsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  let isAP2Enabled = false;
  let isFlagVersion19 = false;
  let isFlagVersion195 = true;
  let isCM3Enabled = false;
  let isNotifiHostedEnabled = false;
  let isFlagVersionQueryFailed = false;
  
  const flags = data?.allTenantFeatureFlags;
  const adminPanelValue = getFlagValue(flags, 'AdminPanel');
  const adminPanelVersion = adminPanelValue ? JSON.parse(adminPanelValue)?.version : undefined;

  if (adminPanelVersion) {
    isAP2Enabled =
      adminPanelVersion === '1.9' ||
      adminPanelVersion === '1.95' ||
      adminPanelVersion === '2.0' ||
      adminPanelVersion === '3.0';

    isFlagVersion19 = adminPanelVersion === '1.9';
    isFlagVersion195 = adminPanelVersion === '1.95';
    isNotifiHostedEnabled =
      adminPanelVersion === '2.0' || adminPanelVersion === '3.0';
    isCM3Enabled =
      adminPanelVersion === '1.9' ||
      adminPanelVersion === '1.95' ||
      adminPanelVersion === '2.0' ||
      adminPanelVersion === '3.0';
  } else if (!loading) {
    isFlagVersionQueryFailed = true;
  }

  const loaded = !loading;
  const isMultilevelTokenFlowForEmailEnabled = parseFeatureFlagToBoolean(
    getFlagValue(flags, 'multilevelTokenFlowForEmail'),
  );
  const canRequire2FA = parseFeatureFlagToBoolean(
    getFlagValue(flags, 'canRequire2FA'),
  );
  const canFilterOnTag = parseFeatureFlagToBoolean(
    getFlagValue(flags, 'canFilterOnTag'),
  );

  const isDiscordGroupsEnabled = parseFeatureFlagToBoolean(
    getFlagValue(flags, 'discordGroupEnabled'),
  );

  return (
    <FeatureFlagContext.Provider
      value={{
        isAP2Enabled,
        isFlagVersion19,
        isFlagVersion195,
        loaded,
        isMultilevelTokenFlowForEmailEnabled,
        canRequire2FA,
        canFilterOnTag,
        isCM3Enabled,
        isNotifiHostedEnabled,
        isFlagVersionQueryFailed,
        isDiscordGroupsEnabled
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = (): FeatureFlagState => {
  return useContext(FeatureFlagContext);
};
